import { type ComponentProps } from 'react';
import dynamic from 'next/dynamic';
import { Collapse } from 'antd';
import { useRouter } from 'next/router';

import { ProgressiveImg } from 'components/base';
import { useI18n } from 'context/i18n';
import { ChevronSVG } from 'components/iconsSVG/chevron';
import { isBlank } from 'utils/StringUtils';
import type { TSeoContent } from 'types/product-list';

const CmsGridBlock = dynamic(() => import('@camper/react-web-components').then((mod) => mod.MOLECULES.Grid), { ssr: false });

type SeoContentProps = {
  camperlab: boolean;
  content: TSeoContent;
};
export function SeoContent({ content, camperlab }: SeoContentProps) {
  const { t } = useI18n();
  const router = useRouter();

  const thirdRowBlank = [isBlank(content?.blockThree?.body), isBlank(content?.blockFour?.body), isBlank(content?.blockFive?.body)].every(
    (item) => item === true,
  );

  return (
    <div
      style={{ '--beige-light': '#efeee8' } as React.CSSProperties}
      className="&[&_h2]:mb-0 mt-8 flex w-full flex-col [&_h3]:mb-0 [&_img]:h-full [&_img]:w-full [&_img]:object-cover [&_p]:mb-0 [&_picture]:h-full [&_picture]:w-full"
    >
      {content?.cms ?
        <>
          <h2 className="px-10 py-8 text-xl font-medium">{t('grid.seo', 'related.products')}</h2>
          <CmsGridBlock {...content.cms} slug={router.asPath.split('?')?.[0] ?? ''} />
        </>
      : null}
      {!isBlank(content?.blockOne?.body) ?
        <div className="grid grid-cols-2 grid-rows-[1fr_300px] sm:grid-rows-[1fr_minmax(300px,500px)] md:grid-cols-3 md:grid-rows-[540px] lg:grid-cols-4 lg:grid-rows-1">
          <div className="overflow-hidden">
            <ProgressiveImg src={content.images?.[0]?.src} alt={content.images?.[0]?.alt ?? ''} />
          </div>
          <div className="flex overflow-hidden md:hidden lg:flex">
            <ProgressiveImg src={content.images?.[1]?.src} alt={content.images?.[1]?.alt ?? ''} />
          </div>
          <div
            className="col-start-1 col-end-3 row-start-1 flex w-full flex-col overflow-hidden bg-[#efeee8] p-6 md:col-start-2 md:col-end-4 lg:col-start-3 lg:col-end-5 [&_h2]:text-2xl md:[&_p]:mt-12 md:[&_p]:px-12 md:[&_p]:py-0 md:[&_p]:pl-8"
            dangerouslySetInnerHTML={{ __html: content.blockOne?.body }}
          ></div>
        </div>
      : null}
      {!isBlank(content?.blockTwo?.body) ?
        <div className="grid grid-cols-2 grid-rows-[1fr_300px] sm:grid-rows-[1fr_minmax(300px,500px)] md:grid-cols-3 md:grid-rows-[540px] lg:grid-cols-4 lg:grid-rows-1">
          <div>
            <ProgressiveImg src={content.images?.[2]?.src} alt={content.images?.[2]?.alt ?? ''} />
          </div>
          <div className="flex overflow-hidden md:hidden lg:flex">
            <ProgressiveImg src={content.images?.[3]?.src} alt={content.images?.[3]?.alt ?? ''} />
          </div>
          <div
            className="col-start-1 col-end-3 row-start-1 flex w-full flex-col overflow-hidden bg-[#efeee8] p-6 [&_h2]:text-2xl md:[&_p]:mt-12 md:[&_p]:px-12 md:[&_p]:py-0 md:[&_p]:pl-8"
            dangerouslySetInnerHTML={{ __html: content.blockTwo?.body }}
          ></div>
        </div>
      : null}
      {!thirdRowBlank ?
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          <div
            className="overflow-hidden bg-[var(--color-brand)] p-6 text-[var(--color-white)] [&_*]:!text-[var(--color-white)] [&_h3]:text-2xl [&_p]:mt-4"
            dangerouslySetInnerHTML={{ __html: content.blockThree?.body }}
          />
          <div
            className="overflow-hidden bg-[var(--color-black)] p-6 text-[var(--color-white)] selection:bg-[var(--color-white)] selection:text-[var(--color-black)] [&_*]:!text-[var(--color-white)] [&_h3]:text-2xl [&_p]:mt-4"
            dangerouslySetInnerHTML={{ __html: content.blockFour?.body }}
          />
          <div className="flex items-center justify-center bg-[#e5dcd3] md:hidden lg:flex">
            <img className="!object-contain" src="/assets-new/svg/grid-seo-recicle.svg" alt="" />
          </div>
          <div className="overflow-hidden p-6 [&_h3]:text-2xl [&_p]:mt-4" dangerouslySetInnerHTML={{ __html: content.blockFive?.body }} />
        </div>
      : null}
      {Array.isArray(content?.faqs) && content.faqs.length > 0 ?
        <div className="mx-auto mt-16 w-full max-w-[896px] px-4">
          <h2 className="px-0 py-8 text-xl font-medium">{content?.faqs_title || t('grid.seo', 'faq.title')}</h2>
          {/* @ts-ignore antd sin tipos correctos */}
          <Collapse
            bordered={false}
            expandIcon={({ isActive }) => (
              <span>
                <ChevronSVG data-active={isActive} className="will-change-transform data-[active=true]:-rotate-180" />
              </span>
            )}
            expandIconPosition="right"
            className="!mt-8 !bg-[var(--background)] [&_h3]:!text-base"
          >
            {content.faqs.map((faq, index) => (
              // @ts-ignore antd sin tipos correctos
              <Collapse.Panel header={<h3>{faq.question}</h3>} key={`faq-${index}`}>
                <p className="text-sm" dangerouslySetInnerHTML={{ __html: faq.answer }} />
              </Collapse.Panel>
            ))}
          </Collapse>
          <script
            id="ldFaq"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: content?.faqs?.map((faq) => ({
                  '@type': 'Question',
                  name: faq.question,
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: faq.answer,
                  },
                })),
              }),
            }}
          />
        </div>
      : null}
      {!camperlab && Array.isArray(content?.links) && content.links.length > 0 ?
        <div className="mx-auto my-16 w-full max-w-[896px] px-4">
          <h2 className="px-0 py-8 text-xl font-medium">{t('grid.seo', 'other.categories')}</h2>
          <div className="mt-8 flex flex-wrap gap-4">
            {content.links.map((link, index) => (
              <a
                className="flex h-10 items-center justify-center border border-[var(--color-black)] px-5 text-[var(--color-black)] transition-colors duration-300 hover:bg-[var(--color-black)] hover:text-[var(--color-white)]"
                href={link.url}
                key={`link-${index}`}
              >
                {link.title}
              </a>
            ))}
          </div>
        </div>
      : null}
    </div>
  );
}
